import { useCallback, useEffect, useRef, useState } from 'react'

import { signInAnonymously } from '../../_data/auth'
import { createOrUpdateQuickPassUser } from '../../_data/quickpass-user'
import { createOrUpdateQuickPassProfile } from '../../_data/quickpass-profile'
import { createAnonSession } from '../../_data/anon-session'
import { writeCrossSiteIdentityToken } from '../../_utilities/cross-site-identity'
import {
  AnonClaimContactMessage,
  AnonContactDetails,
} from '../../_types/anon-session'
import { AnonClaimPageState } from './types'
import {
  ContentCard,
  ErrorBanner,
  Heading,
  PageContainer,
  Spinner,
  Subheading,
  Wordmark,
} from './styled'

const SPINNER_TRANSITION = {
  rotate: 360,
  transition: { repeat: Infinity, duration: 0.7, ease: 'linear' },
} as const

/**
 * First-party popup that turns a no-account renter into a recognised
 * QuickPass identity. Opened by the embedded inquiry form, it:
 *
 *   1. signs the renter in anonymously (a real Firebase UID),
 *   2. receives their typed contact details over a same-origin postMessage,
 *   3. upserts a lite QuickPass user + profile,
 *   4. mints a cross-site session token and drops it in a first-party cookie,
 *   5. hands the token back to the opener and closes.
 *
 * Because this runs as a top-level first-party visit to the QuickPass origin,
 * it establishes the storage state that lets every later host site recognise
 * the renter via the Storage Access API.
 *
 * @returns {JSX.Element}
 */
const AnonClaimPage = (): JSX.Element => {
  const [state, setState] = useState<AnonClaimPageState>({
    status: 'initialising',
    errorMessage: '',
  })
  const initialisationStartedReference = useRef<boolean>(false)
  const contactHandledReference = useRef<boolean>(false)
  const firebaseUidReference = useRef<string>('')

  const postToOpener = useCallback((message: object): void => {
    if (window.opener === null || window.opener === window) {
      return
    }

    window.opener.postMessage(message, window.location.origin)
  }, [])

  const provisionRenter = useCallback(
    async (contact: AnonContactDetails): Promise<void> => {
      try {
        const firebaseAuthUid = firebaseUidReference.current

        if (firebaseAuthUid === '') {
          throw new Error('Anonymous sign-in did not complete.')
        }

        setState({ status: 'provisioning', errorMessage: '' })

        const displayName =
          `${contact.firstName} ${contact.lastName}`.trim() || null

        const user = await createOrUpdateQuickPassUser({
          firebaseAuthUid,
          email: contact.email,
          phone: contact.phone,
          displayName,
          authProviders: [],
        })

        await createOrUpdateQuickPassProfile({
          quickPassUserId: user.id,
          patch: {
            firstName: contact.firstName,
            lastName: contact.lastName,
          },
        })

        const token = await createAnonSession(user.id)

        if (token === null || token === '') {
          throw new Error('QuickPass is unavailable right now.')
        }

        writeCrossSiteIdentityToken(token)

        postToOpener({ source: 'quickpass-anon-claim', type: 'done', token })
        setState({ status: 'done', errorMessage: '' })
        window.close()
      } catch (caughtError: unknown) {
        const description =
          caughtError instanceof Error
            ? caughtError.message
            : 'We could not set up your QuickPass.'

        postToOpener({
          source: 'quickpass-anon-claim',
          type: 'error',
          message: description,
        })
        setState({ status: 'error', errorMessage: description })
      }
    },
    [postToOpener],
  )

  useEffect(() => {
    if (initialisationStartedReference.current === true) {
      return
    }
    initialisationStartedReference.current = true

    const initialise = async (): Promise<void> => {
      if (window.opener === null || window.opener === window) {
        setState({
          status: 'error',
          errorMessage: 'This window was opened incorrectly.',
        })
        return
      }

      try {
        const credential = await signInAnonymously()
        firebaseUidReference.current = credential.user.uid
        setState({ status: 'waiting', errorMessage: '' })
        postToOpener({ source: 'quickpass-anon-claim', type: 'ready' })
      } catch (caughtError: unknown) {
        const description =
          caughtError instanceof Error
            ? caughtError.message
            : 'We could not start your QuickPass.'

        postToOpener({
          source: 'quickpass-anon-claim',
          type: 'error',
          message: description,
        })
        setState({ status: 'error', errorMessage: description })
      }
    }

    void initialise()
  }, [postToOpener])

  useEffect(() => {
    const handleMessage = (event: MessageEvent): void => {
      if (event.origin !== window.location.origin) {
        return
      }

      const data = event.data as Partial<AnonClaimContactMessage>

      if (
        data === null ||
        data.source !== 'quickpass-anon-claim-host' ||
        data.type !== 'contact' ||
        data.payload === undefined
      ) {
        return
      }

      if (contactHandledReference.current === true) {
        return
      }
      contactHandledReference.current = true

      void provisionRenter(data.payload)
    }

    window.addEventListener('message', handleMessage)

    return () => {
      window.removeEventListener('message', handleMessage)
    }
  }, [provisionRenter])

  return (
    <PageContainer>
      <ContentCard>
        <Wordmark>QuickPass</Wordmark>

        {state.status === 'error' ? (
          <>
            <Heading>We hit a snag</Heading>
            <ErrorBanner role="alert">{state.errorMessage}</ErrorBanner>
            <Subheading>You can close this window and try again.</Subheading>
          </>
        ) : (
          <>
            <Heading>Setting up your QuickPass</Heading>
            <Spinner animate={SPINNER_TRANSITION} />
            <Subheading>
              Saving your details so you only ever enter them once&hellip;
            </Subheading>
          </>
        )}
      </ContentCard>
    </PageContainer>
  )
}

export default AnonClaimPage
